import React, { useCallback, useState } from "react";

import { useMutation } from "@apollo/client";
import {
  Close as CloseIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Radio,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { makeStyles } from "tss-react/mui";

import { graphql } from "@/gql";
import useDialogState from "@/hooks/useDialogState";

const useStyles = makeStyles()((theme) => ({
  root: {
    padding: theme.spacing(4),
  },
  action: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "25px",
    border: "1px solid #7D8797",
    padding: theme.spacing(4),
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
      alignItems: "flex-start",
      gap: theme.spacing(3),
    },
  },
  heading: {
    fontWeight: 700,
    fontSize: 28,
    lineHeight: "35px",
    color: "#363E45",
  },
  section: {
    marginTop: "40px",
  },
  subheading: {
    marginBottom: "12px",
    fontWeight: 700,
    fontSize: 16,
    lineHeight: "20px",
    color: "#4C515A",
  },
  body: {
    fontSize: 16,
    lineHeight: "20px",
    color: "#4C515A",
  },
  body2: {
    fontWeight: 600,
    fontSize: 14,
    lineHeight: "17px",
    color: "#0B1B26",
  },
  deleteButton: {
    backgroundColor: "#FF6262",
    "&:hover": {
      backgroundColor: "#FF6262",
    },
  },
  deleteDialog: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    paddingBottom: "40px",
  },
  deleteIcon: {
    fontSize: 60,
    color: "#FFB160",
  },
  deleteTitle: {
    display: "flex",
    justifyContent: "flex-end",
  },
  deleteHeading: {
    marginBottom: "12px",
    fontWeight: 700,
    fontSize: 32,
    lineHeight: "40px",
    color: "#363E45",
    textAlign: "center",
  },
  deleteText: {
    marginBottom: "30px",
    fontWeight: 700,
    fontSize: 20,
    lineHeight: "25px",
    color: "#4C515A",
    textAlign: "center",
  },
  keepButton: {
    marginBottom: "30px",
  },
}));

const deleteAccountMutation = graphql(`
  mutation DeleteAccount {
    deleteAccount(input: {}) {
      ok
    }
  }
`);

const Account: React.FC = () => {
  const { classes } = useStyles();
  const [deleteChecked, setDeleteChecked] = useState(false);
  const deleteDialog = useDialogState();
  const navigate = useNavigate();

  const [_deleteAccount] = useMutation(deleteAccountMutation);

  const deleteAccount = useCallback(async () => {
    const result = await _deleteAccount();
    if (result.data?.deleteAccount?.ok) {
      navigate("/account-deleted");
    }
  }, [_deleteAccount]);

  return (
    <>
      <Paper className={classes.root} elevation={0} square>
        <Typography className={classes.heading}>Account</Typography>

        <div className={classes.section}>
          <Typography className={classes.subheading}>Delete Account</Typography>
          <Typography className={classes.body}>
            Looking to delete your NutriCoach Professionals account? No problem!
            We want to let you know that once you delete your account, all of
            your data will be permanently removed from our system after 30 days.
            So you can feel confident that your information will be completely
            erased from our platform. However, please note that this action
            cannot be undone. So make sure you&apos;ve saved any important
            information before proceeding.
          </Typography>
          <div className={classes.action}>
            <FormControlLabel
              control={
                <Radio
                  color="primary"
                  checked={deleteChecked}
                  onChange={(e) => setDeleteChecked(e.target.checked)}
                />
              }
              label={
                <Typography className={classes.body2}>
                  Yes I understand the consequences on deleting my account
                </Typography>
              }
            />
            <Button
              className={classes.deleteButton}
              disabled={!deleteChecked}
              onClick={deleteDialog.show}
            >
              Delete my Account
            </Button>
          </div>
        </div>
      </Paper>
      <Dialog
        open={deleteDialog.shown}
        onClose={deleteDialog.hide}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle className={classes.deleteTitle}>
          <IconButton size="small" onClick={deleteDialog.hide}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent className={classes.deleteDialog}>
          <WarningIcon className={classes.deleteIcon} />
          <Typography className={classes.deleteHeading}>
            Do you really want to delete your account?
          </Typography>
          <Typography className={classes.deleteText}>
            Once you delete your account, all of your data will be permanently
            removed from our system after 30 days.
          </Typography>
          <Button className={classes.keepButton} onClick={deleteDialog.hide}>
            Keep my account
          </Button>
          <Button className={classes.deleteButton} onClick={deleteAccount}>
            Yes, delete my account
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Account;
